import axios from 'axios';
import moment from 'moment';
import fileDownload from 'js-file-download';
import cloneDeep from 'lodash.clonedeep';
import i18n, { LanguageObject } from '@/i18n';
import { ServiceProvider } from './ProviderUtils';
import { QuestionKey } from '@/components/survey/questions';
import { SubOrganization } from './SubOrganizationUtils';
import { Role } from './RoleUtils';
import { Bookshelf } from './ResourceUtils';
import { DashboardCanvas } from './DashboardUtils';

interface Survey {
  id?: number;
  name: {
    admin: string;
    public: LanguageObject;
  };
  startdate?: string;
  closedate?: string;
  duedate?: string;
  state: 'inactive';
  defaultstate?: Partial<SurveyState>;
  layout: Partial<Section>[];
  surveygroup: Partial<SurveyGroup>;
  options: {
    documents: {
      value: boolean;
      text: LanguageObject;
    };
  };
  filter: {
    providerTypes?: number[];
    oversights?: number[];
  };
  languageobjects: {
    organization: LanguageObject;
    suborg: LanguageObject;
    pointofservice: LanguageObject;
  };
  participantcount?: number;
  bookshelf?: Partial<Bookshelf>;
  dashboard?: {
    canvas: DashboardCanvas;
  };
}

interface SurveyGroup {
  id?: number;
  name: string;
  state: string;
  duedate: string;
  dashboard?: {
    canvas: DashboardCanvas;
  };
}

interface SurveyParticipation {
  id?: number;
  survey: Partial<Survey>;
  serviceprovider: Partial<ServiceProvider>;
  opendate: string;
  startdate: string;
  submitteddate: string;
  completeddate: string;
  createddate: string;
  participationduedate: string;
  state: string;
  submissionstate: Partial<SurveyState>;
  progress?: number;
}

interface SurveyState {
  id?: number;
  key: string;
  survey: Partial<Survey>;
  category: 'NOT STARTED' | 'NOT SUBMITTED' | 'SUBMITTED' | 'COMPLETE';
  name: LanguageObject;
  label: LanguageObject;
  editablebysp: boolean;
  editablebyoversight: boolean;
  spemailkey?: string;
  oversightemailkey?: string;
  nextstate: {
    serviceprovider: SurveyState[];
    oversight: SurveyState[];
  };
}

interface Section {
  type: 'section' | 'page';
  key: string;
  name: LanguageObject;
  title: LanguageObject;
  toc: boolean; // table of contents
  level: 'org' | 'suborg';
  children: Partial<Section | Page>[];
}

interface Page {
  type: 'section' | 'page';
  shortname: LanguageObject;
  longname: LanguageObject;
  helptext: LanguageObject;
  questions: Partial<Question>[];
}

interface Question<Options = any> {
  id?: number;
  survey: Partial<Survey>;
  options: Options;
  questionkey: string;
  questionnumber: string;
  fullquestionnumber: string;
  questiontype: {
    id?: number;
    key: QuestionKey;
    scope?: string[];
  };
  condition: {
    key?: string;
    answers?: string[];
  };
  validation: {
    mandatory: 'none' | 'soft' | 'submit';
  };
  shorttext: LanguageObject;
  longtext: LanguageObject;
  helptext: LanguageObject;
  orglevel?: 'org' | 'suborg';
  permission: {
    visible: Role['type'][];
    editable: Role['type'][];
  };
}

interface QuestionParticipation<Answer = any> {
  id?: number;
  answer: Answer;
  completeness: number; // float 0 -> 1
  survey: Partial<Survey>;
  question: Partial<Question>;
  suborganization?: Partial<SubOrganization>;
  surveyparticipation: Partial<SurveyParticipation>;
}

const api = {
  survey: {
    list: async (params?: { surveygroupid?: number }) => {
      const resp = await axios.get<Survey[]>('/survey/list.php', {
        params: params,
      });
      return resp.data.map((survey) => generateSurvey(survey));
    },

    get: async (id: number) => {
      const resp = await axios.get<Survey>('/survey/get.php', {
        params: { id: id },
      });
      return generateSurvey(resp.data);
    },

    add: async (survey: Survey) => {
      const resp = await axios.post<{ id: number }>(
        '/survey/add.php',
        cleanSurvey(survey)
      );
      return resp.data.id;
    },

    update: async (survey: Partial<Survey>) => {
      await axios.post('/survey/update.php', cleanSurvey(survey));
    },

    remove: async (id: number) => {
      await axios.delete('/survey/delete.php', {
        params: { id: id },
      });
    },
    duplicate: async (data: { id: number; name: Survey['name'] }) => {
      const resp = await axios.post<{ id: number }>(
        '/survey/duplicate.php',
        data
      );
      return resp.data.id;
    },
    validate: async (id: number) => {
      const resp = await axios.get<{ error: boolean; result: string[] }>(
        '/survey/validate.php',
        {
          params: { id: id },
        }
      );
      return resp.data?.result || [];
    },
  },

  surveyQuestionType: {
    list: async () => {
      const resp = await axios.get<Question['questiontype'][]>(
        '/surveyQuestionType/list.php'
      );
      return resp.data;
    },
  },

  surveyGroup: {
    list: async (params?: { nonarchivedonly: boolean }) => {
      const resp = await axios.get<SurveyGroup[]>('/surveyGroup/list.php', {
        params: params,
      });
      return resp.data.map((surveyGroup) => generateSurveyGroup(surveyGroup));
    },

    get: async (id: number) => {
      const resp = await axios.get<SurveyGroup>('/surveyGroup/get.php', {
        params: { id: id },
      });
      return generateSurveyGroup(resp.data);
    },

    add: async (surveygroup: SurveyGroup) => {
      const resp = await axios.post<{ id: number }>(
        '/surveyGroup/add.php',
        cleanSurveyGroup(surveygroup)
      );
      return resp.data.id;
    },

    update: async (surveygroup: Partial<SurveyGroup>) => {
      await axios.post(
        '/surveyGroup/update.php',
        cleanSurveyGroup(surveygroup)
      );
    },

    remove: async (id: number) => {
      await axios.delete('/surveyGroup/delete.php', {
        params: { id: id },
      });
    },
  },

  surveyParticipation: {
    list: async (params?: {
      surveyid?: number;
      serviceproviderid?: number;
      surveygroupid?: number;
      showprogress?: boolean;
      submissionstate?: string;
    }) => {
      const resp = await axios.get<SurveyParticipation[]>(
        '/surveyParticipation/list.php',
        {
          params: params,
        }
      );
      return resp.data.map((participation) =>
        generateSurveyParticipation(participation)
      );
    },

    get: async (id: number) => {
      const resp = await axios.get<SurveyParticipation>(
        '/surveyParticipation/get.php',
        {
          params: { id: id },
        }
      );
      return generateSurveyParticipation(resp.data);
    },

    getProgress: async (params: {
      surveyparticipationid?: number;
      surveyid?: number;
    }) => {
      const resp = await axios.get<number>(
        '/surveyParticipation/getProgress.php',
        {
          params: params,
        }
      );
      return resp.data;
    },

    add: async (
      surveyId: number,
      providers: Partial<ServiceProvider>[],
      groupId?: number,
      questions?: Partial<Question>[]
    ) => {
      const resp = await axios.post<number[]>('/surveyParticipation/add.php', {
        survey: { id: surveyId },
        participants: providers,
        surveygroup: { id: groupId },
        questions: questions?.map((question) => ({
          key: question.questionkey,
          questiontype: question.questiontype?.key,
        })),
      });
      providers = providers.map((provider, index) => ({
        ...provider,
        id: resp.data[index],
      }));
      return providers;
    },

    bulkUpdate: async (
      bulk: Omit<SurveyParticipation, 'id'> & { ids: number[] }
    ) => {
      await axios.post(
        '/surveyParticipation/update.php',
        cleanSurveyParticipation(bulk)
      );
    },

    update: async (participation: Partial<SurveyParticipation>) => {
      await axios.post(
        '/surveyParticipation/update.php',
        cleanSurveyParticipation(participation)
      );
    },

    remove: async (id: number) => {
      await axios.delete('/surveyParticipation/delete.php', {
        params: { id: id },
      });
    },

    duplicate: async (params: {
      survey?: Partial<Survey>;
      surveyparticipation?: Partial<SurveyParticipation>[];
      surveygroup?: Partial<SurveyGroup>;
      serviceprovider?: Partial<ServiceProvider>;
      questions?: Partial<Question>[];
    }) => {
      const resp = await axios.post<any>(
        '/surveyParticipation/duplicateAnswers.php',
        params
      );
      return resp.data;
    },
  },

  surveyState: {
    list: async (params?: { surveyid: number }) => {
      const resp = await axios.get<SurveyState[]>('/surveyState/list.php', {
        params: params,
      });
      return resp.data;
    },

    get: async (id: number) => {
      const resp = await axios.get<SurveyState>('/surveyState/get.php', {
        params: { id: id },
      });
      return resp.data;
    },

    add: async (state: SurveyState) => {
      const stateClone = cloneDeep(state);
      const resp = await axios.post<{ id: number }>('/surveyState/add.php', {
        ...stateClone,
        editablebysp: `${stateClone.editablebysp}`,
        editablebyoversight: `${stateClone.editablebyoversight}`,
      });
      return resp.data.id;
    },

    update: async (state: Partial<SurveyState>) => {
      await axios.post('/surveyState/update.php', {
        ...state,
        editablebysp: `${state.editablebysp}`,
        editablebyoversight: `${state.editablebyoversight}`,
      });
    },

    remove: async (id: number) => {
      await axios.delete('/surveyState/delete.php', {
        params: { id: id },
      });
    },
  },

  question: {
    list: async (params?: { surveyid?: number }) => {
      const resp = await axios.get<Question[]>('/surveyQuestion/list.php', {
        params: params,
      });
      return resp.data;
    },

    get: async (id: number) => {
      const resp = await axios.get<Question>('/surveyQuestion/get.php', {
        params: { id: id },
      });
      return resp.data;
    },

    add: async (question: Question) => {
      const resp = await axios.post<{ id: number }>(
        '/surveyQuestion/add.php',
        question
      );
      return resp.data.id;
    },

    update: async (question: Partial<Question>) => {
      await axios.post('/surveyQuestion/update.php', question);
    },

    remove: async (id: number) => {
      await axios.delete('/surveyQuestion/delete.php', {
        params: { id: id },
      });
    },

    typeList: async () => {
      const resp = await axios.get<any[]>('/surveyQuestionType/list.php');
      return resp.data;
    },

    listTableQuestions: async (params: {
      surveyparticipation: Partial<SurveyParticipation>;
      suborganization?: Partial<SubOrganization>;
      questions: Partial<Question>[];
    }) => {
      const resp = await axios.post<
        {
          suborg: SubOrganization;
          answers: { question: Question; answer: any }[];
        }[]
      >('/surveyQuestion/listTableQuestions.php', {
        language: i18n.locale,
        ...params,
      });
      return resp.data;
    },
  },

  questionParticipation: {
    list: async (params?: {
      surveyid?: number;
      questionid?: number;
      surveyparticipationid?: number;
      suborganizationid?: number | null;
    }) => {
      const resp = await axios.get<QuestionParticipation[]>(
        '/questionParticipation/list.php',
        {
          params: params,
        }
      );
      return resp.data;
    },

    get: async (id: number) => {
      const resp = await axios.get<QuestionParticipation>(
        '/questionParticipation/get.php',
        {
          params: { id: id },
        }
      );
      return resp.data;
    },

    add: async (participations: QuestionParticipation[]) => {
      const resp = await axios.post<number[]>(
        '/questionParticipation/add.php',
        {
          questionparticipations: participations,
        }
      );
      return resp.data;
    },

    update: async (participation: Partial<QuestionParticipation>) => {
      await axios.post('/questionParticipation/update.php', participation);
    },

    remove: async (id: number) => {
      await axios.delete('/questionParticipation/delete.php', {
        params: { id: id },
      });
    },
  },

  getSurveyPage: async (survey: number, page: number) => {
    const resp = await axios.get<Page[]>('/survey/getPage.php', {
      params: {
        id: survey,
        pagenumber: page,
      },
    });
    return resp.data[0];
  },
};

const cleanSurvey = (survey: Partial<Survey>): any => {
  const clone = cloneDeep(survey);

  if (clone.startdate) {
    clone.startdate = moment(clone.startdate)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ssZ'); // convert to utc before sending to api
  }
  if (clone.closedate) {
    clone.closedate = moment(clone.closedate)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ssZ'); // convert to utc before sending to api
  }

  if (clone.duedate) {
    clone.duedate = moment(clone.duedate)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ssZ'); // convert to utc before sending to api
  }

  if (clone.layout) {
    const recursiveLayoutFormatter = (items: Partial<Section | Page>[]) => {
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type === 'section') {
            const section = items[i] as Section;
            recursiveLayoutFormatter(section.children); // depth first looping
          } else if (items[i].type === 'page') {
            const page = items[i] as Page;
            page.questions = (page.questions || []).map((question) => ({
              id: question.id, // only store question ids
            }));
            // TODO: remove page number code as this is just for until it is removed on backend
            (page as any).number = pageNumber;
            pageNumber++;
          }
        }
      }
    };

    let pageNumber = 1; // initial page number
    recursiveLayoutFormatter(clone.layout);
  }
  return clone;
};

const generateSurvey = (json: any): Survey => {
  const survey: Survey = {
    ...json,
    layout: json.layout || [],
    startdate: json.startdate
      ? moment(json.startdate)
          .local()
          .format('YYYY-MM-DD') // convert to local timezone when reading from api
      : // NOTE: if time picker is needed, split startdate into startdate and starttime (so a timepicker component can be used) and merge when sending to api
        undefined,
    closedate: json.closedate
      ? moment(json.closedate)
          .local()
          .format('YYYY-MM-DD') // convert to local timezone when reading from api
      : // NOTE: if time picker is needed, split closedate into closedate and closetime (so a timepicker component can be used) and merge when sending to api
        undefined,
    duedate: json.duedate
      ? moment(json.duedate)
          .local()
          .format('YYYY-MM-DD') // convert to local timezone when reading from api
      : // NOTE: if time picker is needed, split duedate into deudate and duetime (so a timepicker component can be used) and merge when sending to api
        undefined,
  };
  return survey;
};

const generateSurveyGroup = (json: any): SurveyGroup => {
  const surveyGroup: SurveyGroup = {
    ...json,
    duedate: json.duedate
      ? moment(json.duedate)
          .local()
          .format('YYYY-MM-DD') // convert to local timezone when reading from api
      : // NOTE: if time picker is needed, split startdate into startdate and starttime (so a timepicker component can be used) and merge when sending to api
        undefined,
  };
  return surveyGroup;
};

const cleanSurveyGroup = (surveyGroup: Partial<SurveyGroup>): any => {
  const clone = cloneDeep(surveyGroup);

  if (clone.duedate) {
    clone.duedate = moment(clone.duedate)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ssZ'); // convert to utc before sending to api
  }
  return clone;
};

const cleanSurveyParticipation = (
  survey: Partial<SurveyParticipation>
): any => {
  const clone = Object.assign({}, survey);

  if (clone.opendate) {
    clone.opendate = moment(clone.opendate)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ssZ'); // convert to utc before sending to api
  }
  if (clone.startdate) {
    clone.startdate = moment(clone.startdate)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ssZ'); // convert to utc before sending to api
  }
  if (clone.submitteddate) {
    clone.submitteddate = moment(clone.submitteddate)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ssZ'); // convert to utc before sending to api
  }
  if (clone.completeddate) {
    clone.completeddate = moment(clone.completeddate)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ssZ'); // convert to utc before sending to api
  }
  if (clone.createddate) {
    clone.createddate = moment(clone.createddate)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ssZ'); // convert to utc before sending to api
  }
  if (clone.participationduedate) {
    clone.participationduedate = moment(clone.participationduedate)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ssZ'); // convert to utc before sending to api
  }

  return clone;
};

const generateSurveyParticipation = (json: any): SurveyParticipation => {
  const survey: SurveyParticipation = {
    ...json,
    progress: json.progress + '%',
    opendate: json.opendate
      ? moment(json.opendate)
          .local()
          .format('YYYY-MM-DD') // convert to local timezone when reading from api
      : // NOTE: if time picker is needed, split startdate into startdate and starttime (so a timepicker component can be used) and merge when sending to api
        undefined,
    startdate: json.startdate
      ? moment(json.startdate)
          .local()
          .format('YYYY-MM-DD') // convert to local timezone when reading from api
      : // NOTE: if time picker is needed, split startdate into startdate and starttime (so a timepicker component can be used) and merge when sending to api
        undefined,
    submitteddate: json.submitteddate
      ? moment(json.submitteddate)
          .local()
          .format('YYYY-MM-DD') // convert to local timezone when reading from api
      : // NOTE: if time picker is needed, split startdate into startdate and starttime (so a timepicker component can be used) and merge when sending to api
        undefined,
    completeddate: json.completeddate
      ? moment(json.completeddate)
          .local()
          .format('YYYY-MM-DD') // convert to local timezone when reading from api
      : // NOTE: if time picker is needed, split startdate into startdate and starttime (so a timepicker component can be used) and merge when sending to api
        undefined,
    createddate: json.createddate
      ? moment(json.createddate)
          .local()
          .format('YYYY-MM-DD') // convert to local timezone when reading from api
      : // NOTE: if time picker is needed, split closedate into closedate and closetime (so a timepicker component can be used) and merge when sending to api
        undefined,
    participationduedate: json.participationduedate
      ? moment(json.participationduedate)
          .local()
          .format('YYYY-MM-DD') // convert to local timezone when reading from api
      : // NOTE: if time picker is needed, split participationduedate into participationduedate and participationduetime (so a timepicker component can be used) and merge when sending to api
        undefined,
    survey: generateSurvey(json.survey),
  };
  return survey;
};

const flattenLayout = (
  survey: Survey,
  list: Partial<Section | Page>[],
  result?: (Section | Page | Survey)[]
) => {
  if (!result) {
    result = [];
    result.push(survey); // first element is the survey itself, which will cause the table of contents to show on that page
  }
  for (const i in list) {
    if (list[i].type === 'section') {
      // if section has a toc add the section to the array
      if ((list[i] as Section).toc) {
        result.push(list[i] as Section);
      }
      flattenLayout(survey, (list[i] as Section).children, result);
    } else if (list[i].type === 'page') {
      result.push(list[i] as Page);
    }
  }
  return result;
};

const closestVisibleIndex = (
  pages: (Section | Page | Survey)[],
  item: Section | Page | Survey
) => {
  let index = pages.findIndex((val) => val === item);
  if (index === -1) {
    // find the index of whatever child is actually visible as the first page of the section
    const recursiveIndex = (sections: any[]) => {
      let index = pages.findIndex((item) => sections.includes(item));
      if (index < 0) {
        for (let i in sections) {
          if (
            index < 0 &&
            sections[i].children &&
            sections[i].children.length > 0
          ) {
            index = recursiveIndex(sections[i].children);
          }
        }
        return index;
      } else {
        return index;
      }
    };
    index = recursiveIndex((item as Section).children || []);
  }
  return index;
};

const downloadPDF = async (params?: {
  participationid?: number;
  id?: number;
  language?: string;
}) => {
  const resp = await axios.get<any>('/reporting/create.php', {
    params: params,
    responseType: 'blob',
  });
  fileDownload(resp.data, 'report.pdf');
};

export default {
  api,
  downloadPDF,
};

export {
  Survey,
  SurveyGroup,
  SurveyParticipation,
  SurveyState,
  Section,
  Question,
  QuestionParticipation,
  Page,
  flattenLayout,
  closestVisibleIndex,
};
